import React, { useEffect, useRef, useState } from "react";

type EthereumProvider = {
  request: (args: { method: string; params?: unknown[] | object }) => Promise<unknown>;
  on?: (event: string, listener: (...args: any[]) => void) => void;
  removeListener?: (event: string, listener: (...args: any[]) => void) => void;
};
type Wallet = { uuid: string; name: string; provider: EthereumProvider };
type Announcement = { info?: { uuid?: string; name?: string }; provider?: EthereumProvider };
type Props = { onAddressChange?: (address: string | null) => void };

const baseSepolia = {
  chainId: "0x14a34",
  chainName: "Base Sepolia",
  nativeCurrency: { name: "Sepolia Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: ["https://sepolia.base.org"],
  blockExplorerUrls: ["https://sepolia.basescan.org"]
};

export const WalletPicker: React.FC<Props> = ({ onAddressChange }) => {
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [address, setAddress] = useState<string | null>(null);
  const [activeName, setActiveName] = useState("");
  const [chain, setChain] = useState<string | null>(null);
  const [showChoices, setShowChoices] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const activeProvider = useRef<EthereumProvider | null>(null);
  const onChangeRef = useRef(onAddressChange);
  useEffect(() => { onChangeRef.current = onAddressChange; }, [onAddressChange]);

  useEffect(() => {
    const announced = (event: Event) => {
      const detail = (event as CustomEvent<Announcement>).detail;
      if (!detail?.provider || typeof detail.provider.request !== "function") return;
      const uuid = detail.info?.uuid || detail.info?.name || "injected";
      setWallets(current => current.some(w => w.uuid === uuid) ? current :
        [...current, { uuid, name: detail.info?.name || "Browser wallet", provider: detail.provider! }]);
    };
    window.addEventListener("eip6963:announceProvider", announced);
    window.dispatchEvent(new Event("eip6963:requestProvider"));
    const legacy = (window as Window & { ethereum?: EthereumProvider }).ethereum;
    if (legacy && typeof legacy.request === "function") {
      setWallets(current => current.some(w => w.provider === legacy) ? current :
        [...current, { uuid: "legacy", name: "Browser wallet", provider: legacy }]);
    }
    return () => window.removeEventListener("eip6963:announceProvider", announced);
  }, []);

  useEffect(() => {
    const provider = activeProvider.current;
    if (!provider || !address) return;
    const accountsChanged = (accounts: unknown) => {
      const next = Array.isArray(accounts) && typeof accounts[0] === "string" ? accounts[0] : null;
      setAddress(next); onChangeRef.current?.(next);
    };
    const chainChanged = (newChain: unknown) => setChain(typeof newChain === "string" ? newChain : null);
    provider.on?.("accountsChanged", accountsChanged);
    provider.on?.("chainChanged", chainChanged);
    return () => {
      provider.removeListener?.("accountsChanged", accountsChanged);
      provider.removeListener?.("chainChanged", chainChanged);
    };
  }, [address]);

  const connect = async (wallet: Wallet) => {
    setBusy(true); setError("");
    try {
      const accounts = await wallet.provider.request({ method: "eth_requestAccounts" });
      if (!Array.isArray(accounts) || typeof accounts[0] !== "string") throw new Error("Wallet returned no account.");
      const connectedAddress = accounts[0];
      let network = await wallet.provider.request({ method: "eth_chainId" });
      if (network !== baseSepolia.chainId) {
        try {
          await wallet.provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: baseSepolia.chainId }] });
        } catch (err) {
          if ((err as { code?: number })?.code !== 4902) throw err;
          await wallet.provider.request({ method: "wallet_addEthereumChain", params: [baseSepolia] });
          await wallet.provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: baseSepolia.chainId }] });
        }
        network = await wallet.provider.request({ method: "eth_chainId" });
      }
      activeProvider.current = wallet.provider;
      setChain(String(network));
      setAddress(connectedAddress);
      setActiveName(wallet.name);
      onChangeRef.current?.(connectedAddress);
      setShowChoices(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Wallet connection was cancelled.");
    } finally { setBusy(false); }
  };
  const disconnect = () => {
    activeProvider.current = null; setAddress(null); setActiveName(""); setChain(null);
    onChangeRef.current?.(null);
  };
  return <div className="handle-wallet" aria-label="Ethereum wallet connection">
    <div className="handle-wallet-main">
      <div><span className="handle-wallet-eyebrow">WALLET · BASE SEPOLIA</span><strong>{address ? activeName + " · " + address.slice(0,6) + "…" + address.slice(-4) : "Connect your wallet"}</strong></div>
      {address ? <button type="button" className="handle-outline-button" onClick={disconnect}>Disconnect</button> :
        <button type="button" className="handle-dark-button" onClick={() => { setShowChoices(v=>!v); window.dispatchEvent(new Event("eip6963:requestProvider")); }}>Choose wallet</button>}
    </div>
    {address && chain !== baseSepolia.chainId && <p role="alert" className="handle-wallet-alert">Wrong network. Reconnect and switch to Base Sepolia before any testnet actions.</p>}
    {showChoices && !address && <div className="handle-wallet-options">
      <p>Use an installed EVM wallet. This only connects your browser wallet; it does not link or verify it to your Handle account.</p>
      {wallets.map(w => <button key={w.uuid} type="button" disabled={busy} onClick={()=>connect(w)}>{w.name} <span>Connect</span></button>)}
      {!wallets.length && <p>No injected wallets detected. Install a wallet or open Handle inside its wallet browser.</p>}
      <div className="handle-wallet-install">
        <a href="https://www.coinbase.com/wallet" target="_blank" rel="noreferrer">Coinbase Wallet</a>
        <a href="https://metamask.io/" target="_blank" rel="noreferrer">MetaMask</a>
        <a href="https://rabby.io/" target="_blank" rel="noreferrer">Rabby</a>
        <a href="https://rainbow.me/" target="_blank" rel="noreferrer">Rainbow</a>
      </div>
      <small>WalletConnect QR and mobile deep links require a separate SDK configuration; not enabled in this build.</small>
    </div>}
    {error && <p role="alert" className="handle-wallet-alert">{error}</p>}
  </div>;
};
